// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/financeiro/services/contasReceberService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  ContaReceber,
  ResumoFinanceiro,
  ContaReceberResumoDto,
  ContaReceberDetalheDto,
  ContaReceberParcelaDto,
  ContaReceberClienteResumoDto,
  ContaReceberTituloGerencialDto,
  PrazoMedioRecebimentoDto,
  FiltroGerencialReceber,
  CriarContaReceberRequest,
  AtualizarContaReceberRequest,
  BaixarContaReceberRequest,
  BaixarParcelaReceberRequest,
  BaixarParcelasLoteRequest,
  BaixarLoteRequest,
  BaixaResultado,
  BaixaParcelaResultado,
  PaginadoResponse,
  FiltroContasReceber,
  ListaFinanceiraResponse,
} from '../types';

const BASE = '/api/financeiro/contas-receber';

export type { CriarContaReceberRequest };

/** Serializa os filtros gerenciais, omitindo o que é padrão/vazio. */
function paramsGerencial(f: FiltroGerencialReceber) {
  return {
    pagina:         f.pagina ?? 1,
    tamanhoPagina:  f.tamanhoPagina ?? 20,
    busca:          f.busca && f.busca.trim() ? f.busca.trim() : undefined,
    clienteId:      f.clienteId || undefined,
    grupoClienteId: f.grupoClienteId || undefined,
    dtInicio:       f.dtInicio || undefined,
    dtFim:          f.dtFim || undefined,
    baseData:       f.baseData && f.baseData !== 'vencimento' ? f.baseData : undefined,
    situacao:       f.situacao && f.situacao !== 'todos' ? f.situacao : undefined,
    ordenarPor:     f.ordenarPor || undefined,
    direcao:        f.direcao || undefined,
  };
}

export const contasReceberService = {
  async getAll(filtro: FiltroContasReceber = {}): Promise<ListaFinanceiraResponse<ContaReceberResumoDto>> {
    const { pagina = 1, tamanhoPagina = 20, busca, status, dtInicio, dtFim, clienteId,
            ordenarPor, direcao } = filtro;
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaReceberResumoDto>>(BASE, {
      params: {
        pagina,
        tamanhoPagina,
        busca:     busca && busca.trim() ? busca.trim() : undefined,
        status:    status && status !== 'todos' ? status : undefined,
        dtInicio:  dtInicio || undefined,
        dtFim:     dtFim || undefined,
        clienteId: clienteId || undefined,
        // Ordenacao vai para o SERVIDOR: a lista e paginada la.
        ordenarPor: ordenarPor || undefined,
        direcao:    direcao || undefined,
      },
    });
    return data;
  },

  async getById(id: number): Promise<ContaReceber> {
    const { data } = await apiClient.get<ContaReceber>(`${BASE}/${id}`);
    return data;
  },

  async getVencidas(): Promise<ContaReceberResumoDto[]> {
    const { data } = await apiClient.get<PaginadoResponse<ContaReceberResumoDto>>(`${BASE}/vencidas`);
    return data.itens ?? (data as unknown as ContaReceberResumoDto[]);
  },

  async getResumo(): Promise<ResumoFinanceiro> {
    const { data } = await apiClient.get<ResumoFinanceiro>('/api/financeiro/resumo');
    return data;
  },

  /**
   * Listagem da tela operacional: UMA LINHA POR PARCELA (paridade com o desktop).
   * Título legado sem analítica ainda aparece, com `semParcela: true`.
   */
  async getParcelas(
    filtro: FiltroContasReceber = {},
  ): Promise<ListaFinanceiraResponse<ContaReceberParcelaDto>> {
    const { pagina = 1, tamanhoPagina = 20, busca, status, dtInicio, dtFim, clienteId,
            ordenarPor, direcao, incluirVencidos } = filtro;
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaReceberParcelaDto>>(
      `${BASE}/parcelas`,
      {
        params: {
          pagina,
          tamanhoPagina,
          busca:      busca && busca.trim() ? busca.trim() : undefined,
          status:     status && status !== 'todos' ? status : undefined,
          dtInicio:   dtInicio || undefined,
          dtFim:      dtFim || undefined,
          incluirVencidos: incluirVencidos || undefined,
          clienteId:  clienteId || undefined,
          // Só viaja quando não é o padrão — mantém a URL limpa.
          baseData:   filtro.baseData && filtro.baseData !== 'vencimento' ? filtro.baseData : undefined,
          ordenarPor: ordenarPor || undefined,
          direcao:    direcao || undefined,
        },
      },
    );
    return data;
  },

  // ── Visão gerencial: cliente → títulos → parcelas ──────────────────────

  /** Sintético: quanto CADA CLIENTE deve no recorte. */
  async getGerencialClientes(
    filtro: FiltroGerencialReceber = {},
  ): Promise<ListaFinanceiraResponse<ContaReceberClienteResumoDto>> {
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaReceberClienteResumoDto>>(
      `${BASE}/gerencial/clientes`, { params: paramsGerencial(filtro) });
    return data;
  },

  /** Analítico: como está cada ACORDO (total / recebido / saldo). */
  async getGerencialTitulos(
    filtro: FiltroGerencialReceber = {},
  ): Promise<ListaFinanceiraResponse<ContaReceberTituloGerencialDto>> {
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaReceberTituloGerencialDto>>(
      `${BASE}/gerencial/titulos`, { params: paramsGerencial(filtro) });
    return data;
  },

  /** Prazo médio de recebimento (dias entre emissão e recebimento). */
  async getPrazoMedio(filtro: FiltroGerencialReceber = {}): Promise<PrazoMedioRecebimentoDto> {
    const { data } = await apiClient.get<PrazoMedioRecebimentoDto>(
      `${BASE}/gerencial/prazo-medio`, { params: paramsGerencial(filtro) });
    return data;
  },

  async create(conta: CriarContaReceberRequest): Promise<ContaReceberDetalheDto> {
    const { data } = await apiClient.post<ContaReceberDetalheDto>(BASE, conta);
    return data;
  },

  async update(id: number, conta: AtualizarContaReceberRequest): Promise<ContaReceberDetalheDto> {
    const { data } = await apiClient.put<ContaReceberDetalheDto>(`${BASE}/${id}`, conta);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  /** Exclui UMA parcela (o desktop também exclui por parcela). */
  async deleteParcela(parcelaId: number): Promise<void> {
    await apiClient.delete(`${BASE}/parcelas/${parcelaId}`);
  },

  /** Quita o TÍTULO inteiro — operação distinta da baixa de parcela. */
  async baixar(id: number, req: BaixarContaReceberRequest): Promise<BaixaResultado> {
    const { data } = await apiClient.put<BaixaResultado>(`${BASE}/${id}/baixar`, req);
    return data;
  },

  async baixarLote(req: BaixarLoteRequest): Promise<BaixaResultado[]> {
    const { data } = await apiClient.post<BaixaResultado[]>(`${BASE}/baixar-lote`, req);
    return data;
  },

  async estornar(id: number): Promise<BaixaResultado> {
    const { data } = await apiClient.put<BaixaResultado>(`${BASE}/${id}/estorno`);
    return data;
  },

  /** Recebe UMA parcela; `valorPago` menor que o saldo faz baixa parcial. */
  async baixarParcela(
    parcelaId: number, req: BaixarParcelaReceberRequest,
  ): Promise<BaixaParcelaResultado> {
    const { data } = await apiClient.put<BaixaParcelaResultado>(
      `${BASE}/parcelas/${parcelaId}/baixar`, req);
    return data;
  },

  async baixarParcelasLote(req: BaixarParcelasLoteRequest): Promise<BaixaParcelaResultado[]> {
    const { data } = await apiClient.post<BaixaParcelaResultado[]>(
      `${BASE}/parcelas/baixar-lote`, req);
    return data;
  },

  async estornarParcela(parcelaId: number): Promise<BaixaParcelaResultado> {
    const { data } = await apiClient.put<BaixaParcelaResultado>(
      `${BASE}/parcelas/${parcelaId}/estorno`);
    return data;
  },

  async getDetalhe(id: number): Promise<ContaReceberDetalheDto> {
    const { data } = await apiClient.get<ContaReceberDetalheDto>(`${BASE}/${id}/detalhe`);
    return data;
  },
};
